export function sanitizeTrackTitle(raw) {
  if (!raw) return null;
  const descriptorKeywords = [
    'radio edit',
    'edit',
    'live',
    'performance',
    'acoustic',
    'remix',
    'mix',
    'version',
    'instrumental',
    'demo',
    'voice',
    'bonus track',
    'deluxe',
    'extended',
    'karaoke',
    'feat',
    'featuring'
  ];

  const looksLikeDescriptor = value => {
    if (!value) return false;
    const normalized = value.toLowerCase().trim();
    return descriptorKeywords.some(keyword => normalized.includes(keyword));
  };

  let result = String(raw).trim();
  if (!result) return null;

  while (result.includes(' - ')) {
    const lastDash = result.lastIndexOf(' - ');
    const suffix = result.slice(lastDash + 3).trim();
    if (looksLikeDescriptor(suffix)) {
      result = result.slice(0, lastDash).trim();
      continue;
    }
    break;
  }

  const stripDescriptorBlock = pattern =>
    result.replace(pattern, (match, inner) =>
      looksLikeDescriptor(inner) ? '' : match
    );

  result = stripDescriptorBlock(/\(([^)]+)\)/g);
  result = stripDescriptorBlock(/\[([^\]]+)\]/g);
  result = stripDescriptorBlock(/\{([^}]+)\}/g);

  // Strip inline "feat." / "feat " / "featuring" and everything after
  result = result.replace(/\s+feat\.?\s+.*/i, '');
  result = result.replace(/\s+featuring\s+.*/i, '');

  result = result.replace(/\s{2,}/g, ' ').trim();
  return result || raw.trim();
}

export function sanitizeLeverSummary(text) {
  if (!text) return '';
  // Normalize any escaped newlines so we don't carry literal "\n" into outbound emails
  const normalizedText = text
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n');
  const parenPattern = /\s*\([^()]*\)/g;
  return normalizedText
    .split('\n')
    .map(line => {
      const trimmed = line.trim();
      if (!trimmed) return null;

      let cleaned = trimmed;
      let previous;
      do {
        previous = cleaned;
        cleaned = cleaned.replace(parenPattern, '');
      } while (cleaned !== previous);

      cleaned = cleaned.trim().replace(/\s{2,}/g, ' ');
      if (!cleaned) return null;

      if (cleaned.endsWith('.')) {
        cleaned = cleaned.slice(0, -1);
      }

      const segments = cleaned.split(' — ');
      if (segments.length > 2) {
        const first = segments.shift();
        cleaned = `${first} — ${segments.join(', ')}`;
      }

      if (cleaned.startsWith('•')) {
        cleaned = cleaned.replace(/^•\s*/, '• ');
      }

      return cleaned;
    })
    .filter(Boolean)
    .join('\n');
}

export function formatEmotionDescriptor(combo) {
  if (!combo || typeof combo !== 'string') return null;
  const emotionWordMap = {
    dark: 'darkness',
    darkness: 'darkness',
    aggressive: 'aggression',
    aggression: 'aggression',
    intense: 'intensity',
    intensity: 'intensity',
    power: 'power',
    powerful: 'power',
    tender: 'tenderness',
    tenderness: 'tenderness',
    nostalgic: 'nostalgia',
    nostalgia: 'nostalgia',
    wonder: 'wonder',
    peacefulness: 'peacefulness',
    peaceful: 'peacefulness',
    joyful: 'joyfulness',
    joyfulness: 'joyfulness',
    sadness: 'sadness',
    sad: 'sadness',
    tension: 'tension',
    tense: 'tension',
    transcendence: 'transcendence',
    transcendent: 'transcendence',
    bliss: 'bliss',
    blissful: 'bliss',
    gritty: 'grit',
    grit: 'grit',
    energetic: 'energy',
    energy: 'energy'
  };

  const normalizeEmotionWord = value => {
    if (!value) return '';
    const cleaned = value.toLowerCase().replace(/\s+/g, ' ').trim();
    return emotionWordMap[cleaned] || cleaned;
  };

  const parts = combo
    .split('+')
    .map(token => token.replace(/\*/g, '').trim())
    .filter(Boolean);
  if (!parts.length) return null;
  const normalized = parts.map(normalizeEmotionWord);
  if (normalized.length === 1) return normalized[0];
  if (normalized.length === 2) return `${normalized[0]} and ${normalized[1]}`;
  const lead = normalized.slice(0, 2).join(' and ');
  const undertones = normalized.slice(2).join(' and ');
  return `${lead} with undertones of ${undertones}`;
}

export function formatEmotionCombo(emotionCombo) {
  if (!emotionCombo || typeof emotionCombo !== 'string') return '';
  return formatEmotionDescriptor(emotionCombo) || emotionCombo.trim();
}

export function formatEmotionalSignature(rawSignature) {
  if (!rawSignature || typeof rawSignature !== 'string') return null;
  const cleaned = rawSignature.trim();
  if (!cleaned) return null;
  return cleaned;
}

export function shortenArtistName(name) {
  if (!name || typeof name !== 'string') return 'there';
  const trimmed = name.trim();
  if (!trimmed) return 'there';

  const lower = trimmed.toLowerCase();
  // Signals that likely indicate a band/collective; keep full name in those cases
  const bandSignals = [
    ' the ', ' & ', ' and ', ' duo', ' band', ' collective', ' orchestra', ' choir',
    ' crew', ' squad', ' gang', ' group', ' inc', ' llc', ' feat', ' featuring', ' presents', ' vs '
  ];
  // "Josh Todd & The Conflict" → "Josh" (person name + band appendage)
  const ampMatch = trimmed.match(/^(\S+)\s+\S+\s+&\s+/i) || trimmed.match(/^(\S+)\s+\S+\s+and\s+the\s+/i);
  if (ampMatch) {
    return ampMatch[1];
  }

  if (lower.startsWith('the ') || bandSignals.some(sig => lower.includes(sig))) {
    return trimmed;
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 0) return 'there';
  if (parts.length >= 4) return trimmed; // 4+ words = likely band/project name

  const commonFirstNames = new Set([
      // A
      'aaron','abbey','abby','abigail','ada','adam','adele','adrian','adriana','adrienne','agnes','áine',
      'aidan','aiden','aimee','aisha','al','alan','albert','alberto','alejandro','alex','alexander',
      'alexandra','alexis','alfonso','alfred','ali','alice','alicia','alina','alison','allison','alma',
      'alvin','alyssa','amanda','amber','amelia','amy','ana','andre','andrea','andres','andrew','andy',
      'angel','angela','angelica','angelina','angie','anita','ann','anna','anne','annette','anthony',
      'antonio','april','archie','ariana','arianna','ariel','arlene','armando','arnold','arthur','arturo',
      'ashley','asher','astrid','aubrey','audrey','august','augustine','aurora','austin','autumn','ava','avery',
      // B
      'bailey','barbara','barry','beatrice','becky','belinda','bella','ben','benedict','benjamin','bennett','brodie',
      'bernadette','bernard','bernice','bertha','beth','bethany','betty','beverly','bianca','bill','billy',
      'bj','blake','blanche','bo','bob','bobby','bonnie','brad','bradley','brandon','brandy','brenda',
      'brendan','brennan','brent','brett','brian','brianna','bridget','brittany','brittney','brock','brooke',
      'brooklyn','bruce','bruno','bryan','bryant','bryce','byron',
      // C
      'caitlin','caleb','calvin','cameron','camila','camille','candace','candice','cara','carl','carla',
      'carlo','carlos','carmen','carol','carolina','caroline','carolyn','carrie','carson','carter','casey',
      'cassandra','cassidy','catherine','cecelia','cecilia','cedric','celeste','celia','cesar','chad',
      'chance','chandler','charlene','charles','charlie','charlotte','chase','chelsea','cheri','cheryl',
      'chester','chloe','chris','christian','christina','christine','christopher','cindy','claire','clara',
      'clarence','clark','claude','claudia','clay','clayton','cliff','clifford','clifton','clint','clinton',
      'clyde','cody','colby','cole','colette','colin','colleen','collin','colton','connie','connor',
      'conrad','constance','cora','corey','corinne','cornelius','cortney','cory','courtney','craig',
      'cristina','cruz','crystal','curtis','cynthia','cyrus',
      // D
      'daisy','dakota','dale','dallas','dalton','damian','damien','damon','dan','dana','dane','daniel',
      'daniela','danielle','danny','dante','daphne','darius','darlene','darnell','darrell','darren',
      'darryl','darwin','dave','david','dawn','dean','deanna','debbie','deborah','debra','dee','deidre',
      'delilah','della','denise','dennis','derek','derrick','desiree','desmond','destiny','devin','devon',
      'dexter','diana','diane','dianne','dick','diego','dillon','dina','dion','dixie','dolores','domingo',
      'dominic','dominick','dominique','don','donald','donna','donnie','donovan','dora','doreen','doris',
      'dorothy','doug','douglas','drake','drew','duane','duncan','dustin','dwight','dylan',
      // E
      'earl','earnest','eddie','edgar','edith','edmond','edmund','edna','eduardo','edward','edwin',
      'eileen','elaine','eleanor','elena','eli','elias','elijah','elise','eliza','elizabeth','ella',
      'ellen','ellie','elliot','elliott','ellis','elmer','eloise','elsa','elsie','elton','elvis','emanuel',
      'emerson','emilia','emilio','emily','emma','emmanuel','emmett','enrique','eric','erica','erick','erik',
      'erika','erin','ernest','ernesto','ernie','erwin','esperanza','estelle','esther','ethan','ethel',
      'eugene','eunice','eva','evan','evangeline','eve','evelyn','everett','ezekiel','ezra',
      // F
      'fabian','faith','fatima','faye','federico','felicia','felipe','felix','fern','fernanda','fernando',
      'fletcher','flora','florence','floyd','frances','francesca','francine','francis','francisco','frank',
      'frankie','franklin','fred','freddie','frederick','freya','fritz',
      // G
      'gabriel','gabriela','gabriella','gabrielle','gail','gale','garrett','garry','gary','gavin','gayle',
      'gene','genevieve','geoffrey','george','georgia','georgina','gerald','geraldine','gerard','gerardo',
      'gerry','gertrude','gigi','gilbert','gina','ginger','giovanni','gladys','glen','glenn','gloria',
      'gonzalo','gordon','grace','grady','graham','grant','greg','gregory','greta','gretchen','guillermo',
      'gus','gustavo','gwen','gwendolyn',
      // H
      'hailey','haley','hank','hanna','hannah','hans','harlan','harley','harold','harper','harriet',
      'harris','harrison','harry','harvey','hayden','hazel','heath','heather','hector','heidi','helen',
      'helena','helene','helga','henry','herb','herbert','herman','hilda','hillary','holly','homer',
      'hope','horace','howard','hubert','huey','hugh','hugo','humberto','hunter',
      // I
      'ian','ida','ignacio','ike','ilene','imogene','inez','ingrid','ira','irene','iris','irma','irving',
      'irwin','isaac','isabel','isabella','isaiah','isidro','ismael','israel','ivan','ivy',
      // J
      'jace','jack','jackie','jackson','jacob','jacqueline','jade','jaden','jaime','jake','jamal','james',
      'jamie','jan','jana','jane','janelle','janet','janette','janice','janine','janis','jared','jarrett',
      'jasmine','jason','jasper','javier','jay','jayden','jayson','jean','jeanette','jeanne','jeannette',
      'jeannie','jed','jeff','jefferson','jeffery','jeffrey','jenna','jennie','jennifer','jenny','jeremiah',
      'jeremy','jermaine','jerome','jerry','jess','jesse','jessica','jessie','jesus','jewel','jill',
      'jillian','jim','jimmie','jimmy','jo','joan','joann','joanna','joanne','joaquin','jocelyn','jodi',
      'jodie','jody','joe','joel','joey','johanna','john','johnathan','johnnie','johnny','jon','jonah',
      'jonas','jonathan','jonathon','jordan','jorge','jose','joseph','josephine','josh','joshua','josiah',
      'josie','josue','joy','joyce','juan','juanita','jude','judith','judy','julia','julian','juliana',
      'julianne','julie','juliet','juliette','julio','julius','june','junior','justin','justine',
      // K
      'kai','kaitlyn','kara','karen','karina','karl','karla','kasey','kate','katelyn','katherine',
      'kathleen','kathryn','kathy','katie','katrina','kay','kayla','kaylee','keegan','keenan','keith',
      'kelley','kelly','kelsey','kelvin','ken','kendall','kendra','kendrick','kenneth','kenny','kent',
      'kerry','keven','kevin','khloe','kieran','kim','kimberly','king','kirby','kirk','kirsten','kit',
      'korey','kris','krista','kristen','kristi','kristin','kristina','kristine','kristy','krystal',
      'kurt','kurtis','kyle','kylie','kyra',
      // L
      'lacey','lamar','lambo','lana','lance','landon','lane','lara','larissa','larry','latasha','latoya','laura',
      'laurel','lauren','laurence','laurie','laverne','lawrence','layla','lea','leah','leandro','leann',
      'leanna','lee','leigh','lena','lenore','leo','leon','leona','leonard','leonardo','leonel','leopoldo',
      'leroy','les','lesley','leslie','lester','leticia','levi','lewis','liam','lila','lilian','lillian',
      'lillie','lily','lina','lincoln','linda','lindsay','lindsey','lionel','lisa','lloyd','logan','lois',
      'lola','lon','lonnie','lora','lorena','lorenzo','loretta','lori','lorraine','lottie','lou','louie',
      'louis','louisa','louise','lourdes','lucas','lucia','lucille','lucinda','lucy','luis','luisa','luke',
      'luna','luther','lydia','lynda','lynette','lynn',
      // M
      'mabel','mack','mackenzie','macartney','macy','madeleine','madeline','madison','mae','maggie','mahalia','maisie',
      'malcolm','malik','mallory','mandy','manuel','mara','marc','marcel','marcella','marcelo','marcia',
      'marco','marcos','marcus','margaret','margarita','margot','marguerite','maria','mariah','marian',
      'mariana','marianne','marie','marilyn','marina','mario','marion','marisa','marisol','marissa',
      'marjorie','mark','marlene','marlon','marquis','marshall','marta','martha','martin','martina',
      'marty','marvin','mary','maryann','mason','mathew','matt','matthew','maude','maureen','maurice',
      'mauricio','mavis','max','maxine','maxwell','maya','meagan','megan','meghan','melanie','melba',
      'melinda','melissa','melody','melvin','mercedes','meredith','merle','meryl','mia','micah','michael',
      'michaela','michele','michelle','mickey','miguel','mike','mila','milan','mildred','miles','millie',
      'milo','milton','mindy','minerva','minnie','miranda','miriam','misty','mitch','mitchell','molly',
      'mona','monica','monique','monroe','monte','monty','morgan','morris','moses','muriel','murray',
      'myles','myra','myrna','myrtle',
      // N
      'nadia','nadine','nancy','naomi','natalia','natalie','natasha','nate','nathalie','nathan','nathaniel',
      'neal','ned','neil','nellie','nelson','nestor','nicholas','nick','nickolas','nicky','nicolas',
      'nicole','nigel','nina','noah','noel','noelle','nola','nolan','nora','norma','norman',
      // O
      'octavia','octavio','odell','olga','olive','oliver','olivia','omar','opal','ophelia','ora',
      'orlando','oscar','otis','otto','owen',
      // P
      'pablo','paddy','paige','paloma','pamela','paris','parker','pat','patricia','patrick','patsy',
      'patti','patty','paul','paula','pauline','pearl','pedro','peggy','penelope','penny','percy','perry',
      'pete','peter','phil','philip','phillip','phoebe','phyllis','pierre','polly','porter','preston',
      'prince','priscilla',
      // Q
      'queen','quentin','quincy','quinn',
      // R
      'rachel','rafael','ralph','ramiro','ramon','ramona','randall','randolph','randy','raoul','raphael',
      'raquel','rashad','raul','raven','ray','raymond','reagan','rebecca','reed','reggie','reginald',
      'reid','rene','renee','rex','rhonda','ricardo','rich','richard','richie','rick','ricky','rico',
      'riley','rita','rob','robbie','robert','roberta','roberto','robin','rocco','rochelle','rocky','rod',
      'roderick','rodney','rodolfo','rodrigo','rogelio','roger','roland','rolando','roman','romeo','ron',
      'ronald','ronnie','rory','rosa','rosalie','rosalind','rosalyn','rosanna','rose','rosemary','rosetta',
      'rosie','ross','rowan','roxanne','roy','royce','ruben','ruby','rudolph','rudy','rufus','rupert',
      'russell','rusty','ruth','ryan',
      // S
      'sabrina','sadie','sal','sally','salvador','salvatore','sam','samantha','sammie','sammy','samuel',
      'sandra','sandy','santiago','santos','sara','sarah','saul','savannah','scott','sean','sebastian',
      'selena','serena','sergio','seth','seymour','shane','shannon','shari','sharon','shaun','shawn',
      'sheila','shelby','sheldon','shelley','shelly','sheri','sherri','sheryl','shirley','sid','sidney',
      'sierra','sigrid','silvia','simon','simone','silas','solomon','sondra','sonia','sonja','sonya',
      'sophia','sophie','spencer','stacey','staci','stacy','stan','stanford','stanley','stefan','stella',
      'stephan','stephanie','stephen','sterling','steve','steven','stewart','stuart','sue','sullivan',
      'summer','susan','susanna','susanne','susie','suzanne','suzette','sybil','sydney','sylvia',
      // T
      'tabitha','tamara','tami','tamika','tammy','tanya','tara','taryn','tatiana','taylor','ted','teddy',
      'terence','teresa','teri','terra','terrance','terrell','terrence','terri','terry','tessa','thad',
      'thea','thelma','theo','theodore','theresa','therese','thomas','thurman','tiffany','tim','timmy',
      'timothy','tina','titus','tobias','toby','todd','tom','tomas','tommie','tommy','toni','tony',
      'tonya','tracey','traci','tracy','travis','trent','trenton','trevor','trey','tricia','trina',
      'trisha','tristan','troy','trudy','tucker','ty','tyler','tyrone','tyson',
      // U
      'ulysses','ursula',
      // V
      'val','valentina','valeria','valerie','van','vance','vanessa','vaughn','velma','vera','vern',
      'vernon','veronica','vicente','vicki','vicky','victor','victoria','vince','vincent','viola',
      'violet','virgil','virginia','vivian','viviana',
      // W
      'wade','walker','wallace','wally','walter','wanda','ward','warner','warren','wayne','wendell',
      'wendy','wes','wesley','weston','whitney','wilbert','wilbur','wilfred','willa','willard','william',
      'willie','willis','wilma','wilson','winnie','winifred','winston','woodrow','wyatt',
      // X
      'xavier','ximena',
      // Y
      'yolanda','yvette','yvonne',
      // Z
      'zachary','zane','zelda','zoe','zoey','zora'
    ]);

  if (parts.length === 3) {
    const allCaps = parts.every(p => p === p.toUpperCase());
    if (allCaps) return trimmed;
    // Only shorten if the first word is a common first name (person with first/middle/last)
    if (commonFirstNames.has(parts[0].toLowerCase())) return parts[0];
    return trimmed; // Otherwise it's a band name
  }
  if (parts.length === 2) {
    const [first, second] = parts;
    const firstLower = first.toLowerCase();
    const isTitleAlpha = /^\p{Lu}[\p{L}'-]+$/u.test(second);
    if (commonFirstNames.has(firstLower) && isTitleAlpha && second.length > 3) {
      return first;
    }
    // Strip trailing letter variant: "Krystall" → "krystal" which IS in the set
    if (firstLower.length >= 4 && commonFirstNames.has(firstLower.slice(0, -1)) && isTitleAlpha && second.length > 3) {
      return first;
    }
    return trimmed;
  }
  return parts[0];
}

// --- Typo injection for human feel ---

function applyTypo(word) {
  // Transpose two adjacent letters (not the first)
  const transpose = (w) => {
    if (w.length < 3) return null;
    const i = 1 + Math.floor(Math.random() * (w.length - 2));
    return w.slice(0, i) + w[i + 1] + w[i] + w.slice(i + 2);
  };

  // Drop one letter from a double pair: "really" → "realy"
  const dropDouble = (w) => {
    for (let i = 0; i < w.length - 1; i++) {
      if (w[i] === w[i + 1]) {
        return w.slice(0, i) + w.slice(i + 1);
      }
    }
    return null;
  };

  // Skip a letter (not first two or last)
  const skipLetter = (w) => {
    if (w.length < 5) return null;
    const i = 2 + Math.floor(Math.random() * (w.length - 3));
    return w.slice(0, i) + w.slice(i + 1);
  };

  const options = [transpose, dropDouble, skipLetter].sort(() => Math.random() - 0.5);
  for (const fn of options) {
    const result = fn(word);
    if (result && result !== word) return result;
  }
  return word;
}

/**
 * Inject exactly one natural typo into the dynamic body of an email.
 * Only targets lowercase words (4+ chars) in bio/mixer/lyric lines.
 * Skips: greeting, artist/track names (quoted), static template lines, sign-off.
 */
export function injectTypo(text) {
  if (!text) return text;

  // Only target dynamic content (before the first static line). The anchor
  // list must track template copy: the old single "I'm a producer" anchor
  // went stale when the texture pass lowercased the templates, silently
  // disabling typos until the 2026-07-27 read test caught it.
  const STATIC_MARKERS = [
    'i produce and mix',   // artist lanes (gap/niche/over V6)
    "i'm Alexander",       // Alexander manager lane
    "i'm Amanda",          // Amanda manager lane
    "I'm a producer",      // legacy templates
  ];
  let cutoff = -1;
  for (const m of STATIC_MARKERS) {
    const i = text.indexOf(m);
    if (i !== -1 && (cutoff === -1 || i < cutoff)) cutoff = i;
  }
  if (cutoff === -1) return text;

  const dynamicPart = text.slice(0, cutoff);
  const staticPart = text.slice(cutoff);

  // Mask quoted sections (track titles, lyric excerpts) so we don't touch them.
  // Lyric quotes use '...' and often contain apostrophes (don't, can't, etc.)
  // so we match '...<anything>...' as a lyric quote, then plain 'word(s)' for track titles.
  let masked = dynamicPart;
  masked = masked.replace(/'\.\.\.[\s\S]*?\.\.\.'|'[^'\n]{1,50}'/g, m => '_'.repeat(m.length));

  // Find lowercase words 5+ chars (avoids proper nouns, short words that garble badly)
  const wordRegex = /\b([a-z]{5,})\b/g;
  const candidates = [];
  let match;
  while ((match = wordRegex.exec(masked)) !== null) {
    candidates.push({ word: match[1], index: match.index });
  }

  if (candidates.length === 0) return text;

  const pick = candidates[Math.floor(Math.random() * candidates.length)];
  const typo = applyTypo(pick.word);

  if (typo === pick.word) return text;

  return dynamicPart.slice(0, pick.index) + typo + dynamicPart.slice(pick.index + pick.word.length) + staticPart;
}

// Amanda (goldfarb@) subject-line A/B.
// Deterministic 50/50 split by a stable hash of the recipient email: the same
// prospect always gets the same subject, AND the variant is RECOMPUTABLE later
// from the email alone (no DB column needed) so outreach_stats.js can attribute
// replies to a variant after the fact. Replaces the old flat "Artist - Track"
// subject. Both variants are truthful to the email body (a relayed listening
// note from Alexander): A name-drops the principal — artists reply to Alexander,
// not to a rep — while B is a low-key curiosity note. Test which lifts replies.
export function amandaSubjectVariant(email, trackTitle) {
  const track = trackTitle || 'your music';
  const s = String(email || '').toLowerCase();
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  const variant = (h % 2) === 0 ? 'A' : 'B';
  const subject = variant === 'A'
    ? `Alexander flagged '${track}'`
    : `quick note on '${track}'`;
  return { variant, subject };
}

// v7 manager subjects — catalog-anchored, no track name (P3 2026-07-24).
// Same deterministic email-hash split as amandaSubjectVariant so the A/B
// assignment stays recomputable with no schema change.
export function amandaSubjectVariantV7(email, artistName) {
  const artist = artistName || 'your artist';
  const s = String(email || '').toLowerCase();
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  const variant = (h % 2) === 0 ? 'A' : 'B';
  const subject = variant === 'A'
    ? `a number on ${artist}`
    : `quick question about ${artist}`;
  return { variant, subject };
}

export function cleanGenreTokens(raw) {
  if (!raw || typeof raw !== 'string') return [];

  // Generic/useless genre terms to exclude
  const excludedGenres = new Set([
    'singer', 'songwriter', 'singer-songwriter', 'artist', 'music', 'musician',
    'band', 'group', 'solo', 'vocal', 'vocals', 'instrumental', 'acoustic',
    'alternative', 'other', 'others', 'various', 'misc', 'unknown', 'general', 'popular',
    'contemporary', 'modern', 'classic', 'new', 'adult', 'easy listening'
  ]);

  // Split on comma, pipe, and slash to get individual genres
  return raw
    .split(/[,|/]/)
    .map(token => token.trim())
    .filter(token => token.length > 0)
    .filter(token => {
      const lower = token.toLowerCase();
      if (/genre\s*id/i.test(lower)) return false;
      if (/^\d+/.test(lower)) return false;
      if (/^[a-z]{0,2}\d{2,}$/i.test(lower)) return false;
      if (excludedGenres.has(lower)) return false;
      return true;
    });
}
